import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Zap, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/shared/lib/utils'

interface SubscriptionSectionProps {
    planName: string
    creditBalance: number
    maxCredits: number
    isActive: boolean
    isPending?: boolean
    loading?: boolean
}

export const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({
    planName,
    creditBalance,
    maxCredits,
    isActive,
    isPending,
    loading
}) => {
    const percentage = Math.min(Math.round((creditBalance / maxCredits) * 100), 100)

    return (
        <Card className="border-border bg-card shadow-sm overflow-hidden flex flex-col h-full">
            <CardHeader className="pb-3 border-b border-border/50 bg-muted/30">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Zap className="size-4 opacity-70" />
                    Subscription
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-5 space-y-6 flex-1 flex flex-col h-full">
                <div className="space-y-4 flex-1">
                     <div className="flex justify-between items-center">
                        <div className="space-y-0.5">
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Active Plan</p>
                            <h4 className="text-lg font-bold text-foreground">
                                {loading ? '...' : (planName || 'Free Plan')}
                            </h4>
                        </div>
                        <Badge variant="outline" className={cn(
                            "font-bold uppercase text-[9px] tracking-widest px-1.5 h-6",
                            isActive ? "bg-green-500/10 text-green-600 border-green-200" : "bg-muted text-muted-foreground border-border"
                        )}>
                            {isActive ? 'Active' : 'Inactive'}
                        </Badge>
                    </div>

                    <div className="space-y-2">
                        <div className="flex justify-between items-end">
                            <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">Credits remaining</span>
                            <span className="text-sm font-bold tabular-nums">{creditBalance} / {maxCredits}</span>
                        </div>
                        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                            <div 
                                className={cn(
                                    "h-full transition-all duration-700 ease-in-out",
                                    percentage > 80 ? "bg-green-500" : percentage > 30 ? "bg-primary" : "bg-primary/70"
                                )}
                                style={{ width: `${percentage}%` }}
                            />
                        </div>
                    </div>

                    {isPending && (
                        <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-200 animate-pulse text-center">
                            <p className="text-[9px] font-bold text-amber-700 uppercase tracking-widest">
                                Upgrade Pending Confirmation
                            </p>
                        </div>
                    )}
                </div>

                <div className="space-y-3 pt-4 border-t border-border/50 mt-auto">
                    <Link to="/pricing" className="block w-full">
                        <Button variant="outline" className="w-full h-9 border-border bg-card shadow-sm text-xs font-bold gap-2 group p-0">
                            Manage Plan
                            <ExternalLink className="size-3 text-muted-foreground group-hover:text-primary transition-colors" />
                        </Button>
                    </Link>
                    <p className="text-[10px] text-center text-muted-foreground font-medium px-2 leading-relaxed opacity-80">
                        Manage billing, cancel anytime, or switch plans on the pricing dashboard.
                    </p>
                </div>
            </CardContent>
        </Card>
    )
}
